import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, List, ListItemButton, ListItemText, Divider,
  Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, FormControlLabel, Chip, Alert, CircularProgress, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ApiService from '../../services/api.service';

const TypingCenterGroups = () => {
  const [groups, setGroups] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [allCenters, setAllCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', code: '' });

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSelection, setAssignSelection] = useState([]);

  const [ownerOpen, setOwnerOpen] = useState(false);
  const [newOwner, setNewOwner] = useState({ username: '', email: '', fullName: '', password: '' });

  const loadGroups = useCallback(async () => {
    try {
      const res = await ApiService.get('/api/typing-center-groups');
      setGroups(res.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load groups.');
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    try {
      const res = await ApiService.get(`/api/typing-center-groups/${id}`);
      setDetail(res.data?.data || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load group detail.');
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadGroups();
      try {
        const res = await ApiService.getTypingCenters();
        setAllCenters(res.data?.data || []);
      } catch { /* non-fatal */ }
      setLoading(false);
    })();
  }, [loadGroups]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  const handleCreate = async () => {
    setError(''); setInfo('');
    try {
      const res = await ApiService.post('/api/typing-center-groups', newGroup);
      setCreateOpen(false);
      setNewGroup({ name: '', code: '' });
      await loadGroups();
      const created = res.data?.data;
      if (created?.id) setSelectedId(created.id);
      setInfo('Group created.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create group.');
    }
  };

  const openAssign = () => {
    const assigned = (detail?.typingCenters || []).map((c) => c.id);
    setAssignSelection(assigned);
    setAssignOpen(true);
  };

  const toggleCenter = (id) => {
    setAssignSelection((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleAssign = async () => {
    setError(''); setInfo('');
    // Send only the delta: attach the newly-checked, detach the newly-unchecked.
    // Never send a full-membership list, so a bug here can't mass-detach.
    const current = (detail?.typingCenters || []).map((c) => c.id);
    const toAttach = assignSelection.filter((id) => !current.includes(id));
    const toDetach = current.filter((id) => !assignSelection.includes(id));
    try {
      if (toAttach.length) {
        await ApiService.post(`/api/typing-center-groups/${selectedId}/typing-centers/attach`, {
          typingCenterIds: toAttach
        });
      }
      if (toDetach.length) {
        await ApiService.post(`/api/typing-center-groups/${selectedId}/typing-centers/detach`, {
          typingCenterIds: toDetach
        });
      }
      setAssignOpen(false);
      await loadDetail(selectedId);
      await loadGroups();
      setInfo('Typing centers updated.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update typing centers.');
    }
  };

  const handleAddOwner = async () => {
    setError(''); setInfo('');
    try {
      await ApiService.post(`/api/typing-center-groups/${selectedId}/owners`, newOwner);
      setOwnerOpen(false);
      setNewOwner({ username: '', email: '', fullName: '', password: '' });
      await loadDetail(selectedId);
      await loadGroups();
      setInfo('Owner created.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create owner.');
    }
  };

  // Only typing centers (exclude those already in another group are still shown, so
  // the admin can move them). We show all centers and mark current membership.
  const centersInOtherGroup = (c) =>
    c.group_id && detail && c.group_id !== detail.id;

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}

      <Grid container spacing={2}>
        {/* Groups list */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Owner Groups</Typography>
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                New
              </Button>
            </Box>
            <Divider sx={{ mb: 1 }} />
            {groups.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No groups yet.
              </Typography>
            ) : (
              <List dense>
                {groups.map((g) => (
                  <ListItemButton
                    key={g.id}
                    selected={g.id === selectedId}
                    onClick={() => setSelectedId(g.id)}
                  >
                    <ListItemText
                      primary={g.name}
                      secondary={`${g.code} · ${g.typingCenterCount || 0} TCs · ${g.ownerCount || 0} owners`}
                    />
                    {g.active === false && <Chip label="Inactive" size="small" color="default" />}
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Group detail */}
        <Grid item xs={12} md={8}>
          {!detail ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              Select a group to manage its typing centers and owners.
            </Paper>
          ) : (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{detail.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Code: {detail.code}</Typography>

              {/* Typing centers */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Typing Centers</Typography>
                <Button size="small" variant="outlined" onClick={openAssign}>Assign</Button>
              </Box>
              {(detail.typingCenters || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>None assigned.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {detail.typingCenters.map((c) => (
                    <Chip key={c.id} label={`${c.name} (${c.code})`} />
                  ))}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Owners */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Owners</Typography>
                <Button size="small" variant="outlined" startIcon={<PersonAddIcon />} onClick={() => setOwnerOpen(true)}>
                  Add Owner
                </Button>
              </Box>
              {(detail.owners || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">No owners yet.</Typography>
              ) : (
                <List dense>
                  {detail.owners.map((o) => (
                    <React.Fragment key={o.id}>
                      <ListItemText
                        primary={o.fullName || o.username}
                        secondary={`${o.username}${o.email ? ' · ' + o.email : ''}${o.active === false ? ' · inactive' : ''}`}
                      />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Create group dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New Owner Group</DialogTitle>
        <DialogContent>
          <TextField
            label="Name" fullWidth margin="dense"
            value={newGroup.name}
            onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
          />
          <TextField
            label="Code" fullWidth margin="dense"
            value={newGroup.code}
            onChange={(e) => setNewGroup({ ...newGroup, code: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!newGroup.name || !newGroup.code}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign typing centers dialog */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Assign Typing Centers</DialogTitle>
        <DialogContent dividers>
          {allCenters.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No typing centers found.</Typography>
          ) : (
            allCenters.map((c) => {
              const inOther = centersInOtherGroup(c);
              return (
                <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={assignSelection.includes(c.id)}
                        disabled={inOther}
                        onChange={() => toggleCenter(c.id)}
                      />
                    }
                    label={`${c.name} (${c.code})`}
                  />
                  {inOther && (
                    <Tooltip title="Assigned to another group — detach it there first, then assign here">
                      <Chip label="in another group" size="small" color="warning" variant="outlined" />
                    </Tooltip>
                  )}
                </Box>
              );
            })
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Add owner dialog */}
      <Dialog open={ownerOpen} onClose={() => setOwnerOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add Group Owner</DialogTitle>
        <DialogContent>
          <TextField
            label="Full Name" fullWidth margin="dense"
            value={newOwner.fullName}
            onChange={(e) => setNewOwner({ ...newOwner, fullName: e.target.value })}
          />
          <TextField
            label="Username" fullWidth margin="dense"
            value={newOwner.username}
            onChange={(e) => setNewOwner({ ...newOwner, username: e.target.value })}
          />
          <TextField
            label="Email" fullWidth margin="dense"
            value={newOwner.email}
            onChange={(e) => setNewOwner({ ...newOwner, email: e.target.value })}
          />
          <TextField
            label="Password" type="password" fullWidth margin="dense"
            value={newOwner.password}
            onChange={(e) => setNewOwner({ ...newOwner, password: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOwnerOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddOwner}
            disabled={!newOwner.username || !newOwner.password}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TypingCenterGroups;
